import { useState } from 'react'
import type { Dispatch, FC, KeyboardEvent, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { formatCityStatePostal, textEntryIntentProps } from '@messenger/utils'
import {
  EDITOR_FIELD_FINAL_RESTING,
  editorServiceColumnKey,
  editorTextBoxFieldLabel,
  resolveEditorField,
  serviceBlockPartKey,
  serviceDefaultIdKey,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type {
  EditorCaseData,
  EditorSelection,
  EditorTextBoxMount,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import CalendarShell from '@/components/global/Calendar/Calendar'
import {
  AUTHORING_PREVIEW_DEFAULT_STYLE,
  AuthoringFooter,
  AuthoringModalFrame,
  AuthoringPreviewCard,
  seedFieldRun,
  useAuthoringCommit,
} from './EditorTextBoxAuthoringModal'
import type { FieldAddress } from './EditorTextBoxAuthoringModal'
import * as S from './EditorTextBoxModal.styled'

const Calendar = CalendarShell as unknown as FC<{
  date: [Date | null, Dispatch<SetStateAction<Date | null>>]
  show: [boolean, (next: boolean) => void]
}>

export type CompositeFieldKey = 'service' | 'visitation' | 'final-resting'

export const COMPOSITE_FIELD_KEYS: readonly string[] = [
  'service',
  'visitation',
  EDITOR_FIELD_FINAL_RESTING,
]

type CaseFieldWrite = { fieldKey: string; value: string | Date }

type AuthorValues = (writes: readonly CaseFieldWrite[]) => Promise<boolean>

type CompositeDateSupport = {
  getServiceRawDate: (serviceId: string) => Date | null
  formatServiceDate: (date: Date | null) => string
}

type CompositePart = { column: string; label: string }

const SERVICE_TEXT_PARTS: readonly CompositePart[] = [
  { column: 'location', label: 'Location' },
  { column: 'address', label: 'Address' },
  { column: 'city', label: 'City' },
  { column: 'state', label: 'State' },
  { column: 'postal_code', label: 'Postal code' },
  { column: 'start_time', label: 'Start time' },
  { column: 'end_time', label: 'End time' },
]

const INTERMENT_PARTS: readonly CompositePart[] = [
  { column: 'interment_place', label: 'Interment place' },
  { column: 'interment_address', label: 'Interment address' },
  { column: 'interment_city', label: 'Interment city' },
  { column: 'interment_state', label: 'Interment state' },
  { column: 'interment_postal_code', label: 'Interment postal code' },
]

type CompositeFieldModalProps = {
  getMount: () => EditorTextBoxMount | null
  getHostEl: () => HTMLDivElement | null
  caseData?: EditorCaseData
  savedSel?: EditorSelection | null
  fieldKey: CompositeFieldKey
  serviceId?: string | null
  /** The block run the modal was opened on; unset, the box's first run of the key. */
  field?: FieldAddress | null
  onAuthorValues?: AuthorValues
  dateSupport?: CompositeDateSupport | null
  onClose: () => void
}

const CompositeFieldModal = ({
  getMount,
  getHostEl,
  caseData = {},
  savedSel = null,
  fieldKey,
  serviceId = null,
  field = null,
  onAuthorValues,
  dateSupport = null,
  onClose,
}: CompositeFieldModalProps) => {
  const { t } = useTranslation()
  const label = editorTextBoxFieldLabel(t, fieldKey)
  const isServiceKind = fieldKey === 'service' || fieldKey === 'visitation'
  const serviceKind = fieldKey === 'visitation' ? 'visitation' : 'service'
  const defaultServiceId = isServiceKind
    ? (caseData[serviceDefaultIdKey(serviceKind)] ?? '')
    : ''
  const targetServiceId = isServiceKind
    ? (serviceId ?? '') !== ''
      ? (serviceId as string)
      : defaultServiceId
    : ''
  const hasTarget = !isServiceKind || targetServiceId !== ''

  const parts = isServiceKind ? SERVICE_TEXT_PARTS : INTERMENT_PARTS
  const partAuthorKey = (column: string): string =>
    isServiceKind ? editorServiceColumnKey(targetServiceId, column) : column
  const canAuthor = hasTarget && typeof onAuthorValues === 'function'

  const [seededValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      parts.map((part) => [
        part.column,
        caseData[partAuthorKey(part.column)] ?? '',
      ]),
    ),
  )
  const [values, setValues] = useState<Record<string, string>>(seededValues)

  const [initialDate] = useState<Date | null>(() =>
    isServiceKind && hasTarget
      ? (dateSupport?.getServiceRawDate?.(targetServiceId) ?? null)
      : null,
  )
  const [pickedDate, setPickedDate] = useState<Date | null>(initialDate)
  const dateTime = (d: Date | null): number | null =>
    d instanceof Date ? d.getTime() : null
  const dateDirty =
    isServiceKind && dateTime(pickedDate) !== dateTime(initialDate)

  const [seed] = useState<FieldAddress | null>(() => {
    const run = seedFieldRun(getMount(), fieldKey, field)
    if (!run) return null
    if (isServiceKind) {
      const runServiceId =
        (run.params as { serviceId?: string } | undefined)?.serviceId ??
        defaultServiceId
      if (runServiceId !== targetServiceId) return null
    }
    return { blockIndex: run.blockIndex ?? 0, runIndex: run.runIndex }
  })

  const { saving, error, run } = useAuthoringCommit()
  const editMode = seed !== null

  const draftCaseData = (): EditorCaseData => {
    if (!hasTarget) return caseData
    if (!isServiceKind) {
      return {
        ...caseData,
        interment_place: values.interment_place.trim(),
        interment_address: values.interment_address.trim(),
        interment_city_state_postal: formatCityStatePostal({
          city: values.interment_city,
          state: values.interment_state,
          postal: values.interment_postal_code,
        }),
      }
    }
    const key = (part: string): string =>
      serviceBlockPartKey(targetServiceId, part)
    return {
      ...caseData,
      [key('location')]: values.location.trim(),
      [key('address')]: values.address.trim(),
      [key('city_state_postal')]: formatCityStatePostal({
        city: values.city,
        state: values.state,
        postal: values.postal_code,
      }),
      [key('date')]: pickedDate
        ? (dateSupport?.formatServiceDate?.(pickedDate) ?? '')
        : '',
      [key('start_time')]: values.start_time.trim(),
      [key('end_time')]: values.end_time.trim(),
    }
  }

  const previewText = resolveEditorField(
    fieldKey,
    draftCaseData(),
    isServiceKind && hasTarget ? { serviceId: targetServiceId } : undefined,
  )

  const restoreCaret = () => {
    getHostEl()?.focus()
    getMount()?.placeCaretAtPos(savedSel?.focus ?? { blockIndex: 0, offset: 0 })
  }
  const close = () => {
    restoreCaret()
    onClose()
  }

  const commitValues = async (): Promise<boolean> => {
    if (!canAuthor) return true
    const writes: CaseFieldWrite[] = []
    for (const part of parts) {
      const next = (values[part.column] ?? '').trim()
      if (next !== (seededValues[part.column] ?? '').trim())
        writes.push({ fieldKey: partAuthorKey(part.column), value: next })
    }
    if (dateDirty && pickedDate)
      writes.push({ fieldKey: partAuthorKey('date'), value: pickedDate })
    if (writes.length === 0) return true
    return run(
      () => onAuthorValues!(writes),
      t('editor.textBox.modals.composite.saveError'),
    )
  }

  const insertBlock = async () => {
    if (!(await commitValues())) return
    getHostEl()?.focus()
    if (savedSel) getMount()?.placeRangeAtPos(savedSel.anchor, savedSel.focus)
    getMount()?.insertField(
      fieldKey,
      isServiceKind &&
        targetServiceId !== '' &&
        targetServiceId !== defaultServiceId
        ? { serviceId: targetServiceId }
        : undefined,
    )
    onClose()
  }

  const saveBlock = async () => {
    if (!(await commitValues())) return
    close()
  }

  const dirty =
    (dateDirty && pickedDate !== null) ||
    parts.some(
      (part) =>
        (values[part.column] ?? '').trim() !==
        (seededValues[part.column] ?? '').trim(),
    )

  const convertBlock = () => {
    if (seed) getMount()?.convertFieldToText(seed.blockIndex, seed.runIndex)
    close()
  }

  const onPartKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      if (editMode) saveBlock()
      else insertBlock()
    }
  }

  return (
    <AuthoringModalFrame
      backdropTestId="data-editor-text-composite-modal-backdrop"
      onDismiss={close}
      dialogAttrs={{
        'data-editor-text-composite-modal': true,
        'data-composite-field': fieldKey,
        ...(isServiceKind && hasTarget
          ? { 'data-composite-service-id': targetServiceId }
          : {}),
      }}
      ariaLabel={label}
      title={label}
      subtitle={
        editMode
          ? t('editor.textBox.modals.composite.subtitleEdit')
          : t('editor.textBox.modals.common.subtitleInsert')
      }
      closeAttrs={{ 'data-editor-text-composite-close': true }}
      footer={
        <AuthoringFooter
          danger={
            editMode ? (
              <S.DangerBtn
                type="button"
                data-editor-text-composite-convert
                title={t('editor.textBox.modals.composite.convertTitle')}
                onClick={convertBlock}
              >
                {t('editor.textBox.modals.common.convertToText')}
              </S.DangerBtn>
            ) : null
          }
          cancelAttrs={{ 'data-editor-text-composite-cancel': true }}
          onCancel={close}
          secondary={
            editMode || !canAuthor
              ? null
              : {
                  attrs: { 'data-editor-text-composite-save-only': true },
                  label: t('editor.textBox.modals.common.saveToCase'),
                  title: dirty
                    ? t('editor.textBox.modals.composite.saveOnlyTitle')
                    : t('editor.textBox.modals.composite.unchangedTitle'),
                  disabled: !dirty,
                  onClick: saveBlock,
                }
          }
          primaryAttrs={
            editMode
              ? { 'data-editor-text-composite-save': true }
              : { 'data-editor-text-composite-insert': true }
          }
          primaryLabel={
            editMode
              ? t('editor.textBox.modals.common.saveChanges')
              : t('editor.textBox.modals.common.insertField')
          }
          onPrimary={editMode ? saveBlock : insertBlock}
          saving={saving}
        />
      }
    >
      <S.SplitBody>
        <S.SplitValueCol>
          <S.SectionLabel>
            {t('editor.textBox.modals.common.values')}
          </S.SectionLabel>
          {!hasTarget ? (
            <S.Hint data-editor-text-composite-no-target>
              {t('editor.textBox.modals.composite.noTarget', {
                label,
                labelLower: label.toLowerCase(),
              })}
            </S.Hint>
          ) : (
            <>
              {parts.map((part) => (
                <S.CompositePart key={part.column}>
                  <S.CompositePartLabel>
                    {t(`editor.textBox.modals.composite.parts.${part.column}`, {
                      defaultValue: part.label,
                    })}
                  </S.CompositePartLabel>
                  <S.ValueInput
                    data-composite-value={part.column}
                    {...textEntryIntentProps()}
                    type="text"
                    value={values[part.column] ?? ''}
                    placeholder="—"
                    onChange={(e) =>
                      setValues((v) => ({
                        ...v,
                        [part.column]: e.target.value,
                      }))
                    }
                    onKeyDown={onPartKeyDown}
                  />
                </S.CompositePart>
              ))}
              {isServiceKind && (
                <S.CompositePart as="div">
                  <S.CompositePartLabel>
                    {t('editor.textBox.modals.common.date')}
                  </S.CompositePartLabel>
                  <div data-editor-text-composite-datepicker>
                    <Calendar
                      date={[pickedDate, setPickedDate]}
                      show={[true, () => {}]}
                    />
                  </div>
                </S.CompositePart>
              )}
            </>
          )}
        </S.SplitValueCol>
        <S.SplitSideCol>
          <S.SectionLabel>
            {t('editor.textBox.modals.common.preview')}
          </S.SectionLabel>
          <AuthoringPreviewCard
            effective={AUTHORING_PREVIEW_DEFAULT_STYLE}
            text={previewText}
            ghost={t('editor.textBox.modals.composite.ghost', { label })}
            cardAttrs={{ 'data-editor-text-composite-preview': true }}
            textAttrs={{ 'data-editor-text-composite-preview-text': true }}
            ghostAttrs={{ 'data-editor-text-composite-preview-ghost': true }}
            cardProps={{ $block: true }}
            textProps={{ $block: true }}
          />
          <S.Hint>
            {canAuthor
              ? t('editor.textBox.modals.composite.hintEditable')
              : t('editor.textBox.modals.composite.hintReadOnly')}
          </S.Hint>
          {error && (
            <S.ErrorText data-editor-text-composite-error>{error}</S.ErrorText>
          )}
        </S.SplitSideCol>
      </S.SplitBody>
    </AuthoringModalFrame>
  )
}

export default CompositeFieldModal
